import { BOSS_TIERS, type BossTier, DEFAULT_BOSS_TIER } from '../boss/tier';
import { CLOSED_ALPHA_MAP_COUNT } from '../catalog';
import {
  EQUIPMENT_COMPONENTS,
  EQUIPMENT_RARITIES,
  type EquipmentComponent,
  type EquipmentRarity,
} from '../equipment/catalog';
import { dropIdFor, type KillIdentity } from './loot';

/**
 * The special drop: the five energy components and the Quantum Stabiliser.
 *
 * ## Why this is a second table and not a wider artifact table
 *
 * Artifacts already roll once per confirmed kill against their own
 * denominators, and that roll is a decided, shipped, audited thing. Widening it
 * would mean re-deciding artifact odds every time equipment odds move, and the
 * two have nothing to do with each other. This is an INDEPENDENT roll on the
 * same kill: a kill can drop an artifact and a component, and neither changes
 * the other's chance.
 *
 * ## One roll, mutually exclusive outcomes
 *
 * A kill draws ONE integer. Where it lands decides component, stabiliser or
 * nothing, so the two can never both happen on the same kill by construction
 * rather than by a check somebody could forget. That is also why the odds are
 * written as slices of a single denominator instead of two independent
 * percentages - two independent draws would have a small chance of both hitting
 * and the rule would then live in whichever branch remembered to handle it.
 *
 * ## Integers only
 *
 * No floating point anywhere on this path. `0.25%` is 250 parts in 100 000, not
 * a double that happens to round the right way; the tests assert the slices sum
 * to the denominator exactly, which is a statement a float cannot support.
 */

/*
 * Re-exported, not redeclared: existing call sites import the tier from here
 * and must keep working, while there remains exactly one declaration.
 */
export { BOSS_TIERS, type BossTier, DEFAULT_BOSS_TIER };

/** What one special roll decided. Exactly one of these, never two. */
export type EquipmentDropKind = 'component' | 'stabilizer' | 'none';

/**
 * A normal mob kill, as slices of 300 000.
 *
 * 50/300 000 is 1/6 000 and 6/300 000 is 1/50 000, both exactly. 300 000 is the
 * smallest denominator that expresses both without a remainder, which is the
 * only reason it is the number.
 */
export const EQUIPMENT_NORMAL_DROP = Object.freeze({
  denominator: 300_000,
  /** 1 in 6 000. */
  componentOutcomes: 50,
  /** 1 in 50 000. */
  stabilizerOutcomes: 6,
});

/*
 * The tier itself now lives in `../boss/tier`.
 *
 * It was declared here because loot was the first thing to need it, and that
 * was the wrong home: a tier decides how big a boss is drawn and what Gold it
 * guarantees, and these odds are the third thing to read it. Two modules each
 * declaring a six-value `BossTier` is how those two values drift apart.
 */

/**
 * The rare special draw's odds per tier, as slices of 100 000.
 *
 * ONLY odds. Size, appearance frequency and Gold belong to the tier itself and
 * live in `../boss/tier`; a loot table that also carried them would be two
 * unrelated decisions sharing one literal, and editing either would risk the
 * other.
 *
 * Every percentage in the design lands on a whole number of parts at this
 * denominator - 0,25% is 250, 4,5% is 4 500, 0,65% is 650 - so the table needs
 * no rounding rule and none is invented. These values are frozen by
 * `equipment-loot-frozen.test.ts` and are unchanged by this rename.
 */
export const BOSS_SPECIAL_DROP_ODDS_BY_TIER = Object.freeze({
  denominator: 100_000,
  byTier: Object.freeze({
    1: Object.freeze({ componentOutcomes: 2_000, stabilizerOutcomes: 250 }),
    2: Object.freeze({ componentOutcomes: 3_000, stabilizerOutcomes: 400 }),
    3: Object.freeze({ componentOutcomes: 4_500, stabilizerOutcomes: 650 }),
    4: Object.freeze({ componentOutcomes: 6_000, stabilizerOutcomes: 1_000 }),
    5: Object.freeze({ componentOutcomes: 8_000, stabilizerOutcomes: 1_500 }),
    6: Object.freeze({ componentOutcomes: 10_000, stabilizerOutcomes: 2_000 }),
  }) as Readonly<
    Record<BossTier, { readonly componentOutcomes: number; readonly stabilizerOutcomes: number }>
  >,
});

/**
 * The rarity of a component dropped on MAP 1, as slices of 1 000 000.
 *
 * Named for its map because it belongs to one. The order is the rarity ladder,
 * and the bands are laid out in that order so a draw maps to a rarity by walking
 * the cumulative sum - which is what makes "the slices must total the
 * denominator" a real invariant rather than a comment.
 *
 * ## Ja nao e a tabela da CACA (gate V28I.BP)
 *
 * "Later maps will have their own tables" era a frase que estava aqui, e os
 * vinte mapas usavam esta mesma. Eles passaram a usar
 * {@link componentRarityTableForMap}, derivada do indice.
 *
 * Esta constante FICA, e sem uma virgula mudada, porque o mundo aberto a
 * consome sob outro nome: `PLANET_COMPONENT_RARITY` e ela, e a economia de
 * mineracao de planetas ja foi aprovada e nao e assunto deste gate.
 */
export const MAP_1_COMPONENT_RARITY = Object.freeze({
  denominator: 1_000_000,
  bands: Object.freeze([
    Object.freeze({ rarity: 'uncommon' as EquipmentRarity, outcomes: 700_000 }),
    Object.freeze({ rarity: 'rare' as EquipmentRarity, outcomes: 200_000 }),
    Object.freeze({ rarity: 'epic' as EquipmentRarity, outcomes: 70_000 }),
    Object.freeze({ rarity: 'mythic' as EquipmentRarity, outcomes: 20_000 }),
    Object.freeze({ rarity: 'legendary' as EquipmentRarity, outcomes: 7_000 }),
    Object.freeze({ rarity: 'excellent' as EquipmentRarity, outcomes: 2_000 }),
    Object.freeze({ rarity: 'perfect' as EquipmentRarity, outcomes: 800 }),
    Object.freeze({ rarity: 'anomaly' as EquipmentRarity, outcomes: 190 }),
    Object.freeze({ rarity: 'prismatic' as EquipmentRarity, outcomes: 10 }),
  ]),
});

/* -------------------------------------------------------------------------- */
/*  A janela de raridade de cada mapa                                         */
/* -------------------------------------------------------------------------- */

/**
 * **Quatro raridades por mapa, e a janela desliza** (gate V28I.BP).
 *
 * ## O defeito que isto fecha
 *
 * `MAP_1_COMPONENT_RARITY` era usada em TODOS os vinte mapas. Um mapa 20 com
 * 299,5x a vida do mapa 1 largava exactamente a mesma distribuicao — 70% de
 * Uncommon — e o mapa 1 podia largar um Prismatic a uma em cem mil. As duas
 * pontas estavam erradas ao mesmo tempo.
 *
 * ## Uma REGRA, e nao uma segunda tabela de vinte linhas
 *
 * A janela e derivada do indice: quatro raridades consecutivas na ordem
 * canonica de `EQUIPMENT_RARITIES`, com os mesmos quatro pesos em todos os
 * mapas. Vinte linhas escritas a mao seriam vinte oportunidades de um peso nao
 * somar, e o dono aprovou a REGRA e nao a transcricao dela.
 *
 * | mapas | janela dos mobs |
 * | --- | --- |
 * | 1-2 | uncommon - rare - epic - mythic |
 * | 3-6 | rare - epic - mythic - legendary |
 * | 7-10 | epic - mythic - legendary - excellent |
 * | 11-14 | mythic - legendary - excellent - perfect |
 * | 15-18 | legendary - excellent - perfect - anomaly |
 * | 19-20 | excellent - perfect - anomaly - prismatic |
 *
 * ## Nove raridades, e `common` nao e uma delas
 *
 * `EQUIPMENT_RARITIES` e `RARITIES` sem `common`: componentes Common nao
 * existem e nao estao pendentes. A janela desliza dentro dessas nove.
 *
 * ## O que NAO muda
 *
 * A chance de EXISTIR uma queda. `EQUIPMENT_NORMAL_DROP` e
 * `BOSS_SPECIAL_DROP_ODDS_BY_TIER` ficam exactamente onde estavam: isto decide
 * apenas o que o componente e, **depois** de a queda ja ter sido concedida.
 */

/** Os quatro pesos, em partes de um milhao. Somam exactamente o denominador. */
export const COMPONENT_RARITY_WEIGHTS = Object.freeze([600_000, 250_000, 110_000, 40_000]);

/** Quantas raridades uma janela tem. */
export const COMPONENT_RARITY_WINDOW = COMPONENT_RARITY_WEIGHTS.length;

/** O denominador de uma distribuicao de raridade. O mesmo do mapa 1. */
export const COMPONENT_RARITY_DENOMINATOR = 1_000_000;

/**
 * O TOPO da janela de um mapa, como indice em `EQUIPMENT_RARITIES`.
 *
 * Mapas 1-2 no indice 3 (mythic) e depois um degrau a cada quatro mapas, ate
 * 8 (prismatic) nos mapas 19-20. Aritmetica inteira, sem tabela.
 */
function windowTopIndex(mapIndex: number): number {
  const band = mapIndex <= 2 ? 0 : Math.min(5, Math.floor((mapIndex - 3) / 4) + 1);
  return COMPONENT_RARITY_WINDOW - 1 + band;
}

export interface ComponentRarityBand {
  readonly rarity: EquipmentRarity;
  readonly outcomes: number;
}

export interface ComponentRarityTable {
  readonly denominator: number;
  readonly bands: readonly ComponentRarityBand[];
}

/**
 * **A tabela de raridade de um mapa**, para um mob ou para um chefe.
 *
 * O chefe usa a MESMA janela deslocada uma posicao para cima na ordem canonica,
 * e o deslocamento e por POSICAO — nao ha uma segunda tabela a manter alinhada
 * com a primeira.
 *
 * ## O encosto no topo, e porque ele SOMA as parcelas em vez de as cortar
 *
 * Nos mapas 19-20 a janela dos mobs ja acaba em `prismatic`, portanto a do
 * chefe sairia de la. Ela encosta: as posicoes que passariam do topo caem todas
 * em `prismatic` e as parcelas somam-se. O chefe do mapa 20 fica com perfect
 * 60%, anomaly 25% e prismatic 15% — o 11% mais o 4% —, e a soma continua a ser
 * exactamente o denominador. Cortar as parcelas deixaria a distribuicao a somar
 * 85% e o ultimo ramo de `pickComponentSpecies` a nunca ser alcancado por uma
 * fatia do sorteio.
 */
export function componentRarityTableForMap(
  mapIndex: number,
  wasBoss: boolean,
): ComponentRarityTable {
  if (!Number.isSafeInteger(mapIndex) || mapIndex < 1 || mapIndex > CLOSED_ALPHA_MAP_COUNT) {
    throw new RangeError(
      `mapIndex must be an integer in [1, ${CLOSED_ALPHA_MAP_COUNT}], got ${mapIndex}`,
    );
  }
  const top = windowTopIndex(mapIndex) + (wasBoss ? 1 : 0);
  const ceiling = EQUIPMENT_RARITIES.length - 1;
  const bands: ComponentRarityBand[] = [];
  for (let offset = 0; offset < COMPONENT_RARITY_WINDOW; offset += 1) {
    const index = Math.min(ceiling, top - (COMPONENT_RARITY_WINDOW - 1 - offset));
    const rarity = EQUIPMENT_RARITIES[index];
    if (rarity === undefined) {
      throw new RangeError(`no equipment rarity at index ${index}`);
    }
    const outcomes = COMPONENT_RARITY_WEIGHTS[offset] ?? 0;
    const last = bands[bands.length - 1];
    if (last !== undefined && last.rarity === rarity) {
      /* O encosto juntou duas posicoes: as parcelas somam-se, nunca se perdem. */
      bands[bands.length - 1] = { rarity, outcomes: last.outcomes + outcomes };
      continue;
    }
    bands.push({ rarity, outcomes });
  }
  return { denominator: COMPONENT_RARITY_DENOMINATOR, bands };
}

/**
 * **As vinte janelas sao sas, e prova-se ao carregar.**
 *
 * Corre no arranque e nao numa suite, pela mesma razao que as provas do
 * catalogo de peixes: uma janela mal derivada tem de falhar antes de chegar a
 * um jogador. Afirma tudo o que o gate exigiu que fosse verificavel.
 */
function assertComponentRarityWindowsAreSound(): void {
  if (
    COMPONENT_RARITY_WEIGHTS.reduce((total, weight) => total + weight, 0) !==
    COMPONENT_RARITY_DENOMINATOR
  ) {
    throw new RangeError('component rarity weights must total the denominator');
  }
  for (let mapIndex = 1; mapIndex <= CLOSED_ALPHA_MAP_COUNT; mapIndex += 1) {
    for (const wasBoss of [false, true]) {
      const table = componentRarityTableForMap(mapIndex, wasBoss);
      const total = table.bands.reduce((sum, band) => sum + band.outcomes, 0);
      if (total !== COMPONENT_RARITY_DENOMINATOR) {
        throw new RangeError(
          `map ${mapIndex} ${wasBoss ? 'boss' : 'mob'} rarity bands total ${total}`,
        );
      }
      for (const band of table.bands) {
        if (!(EQUIPMENT_RARITIES as readonly string[]).includes(band.rarity)) {
          throw new RangeError(`map ${mapIndex} produced a non-equipment rarity: ${band.rarity}`);
        }
      }
    }
  }
  /* O mapa 1 nunca produz o topo, e o mapa 20 nunca produz o fundo. */
  const firstMob = componentRarityTableForMap(1, false);
  if (firstMob.bands.some((band) => band.rarity === 'prismatic')) {
    throw new RangeError('map 1 must not be able to drop a prismatic component');
  }
  const lastMob = componentRarityTableForMap(CLOSED_ALPHA_MAP_COUNT, false);
  if (!lastMob.bands.some((band) => band.rarity === 'prismatic')) {
    throw new RangeError('map 20 must be able to drop a prismatic component');
  }
  if (lastMob.bands.some((band) => band.rarity === 'uncommon')) {
    throw new RangeError('map 20 must not be able to drop an uncommon component');
  }
}

assertComponentRarityWindowsAreSound();

/**
 * The message this roll is keyed on.
 *
 * A DIFFERENT string from the artifact roll's, over the same session secret.
 * That is the whole reason the artifact stream is untouched by this feature:
 * HMAC over a different message is an independent output, so adding equipment
 * drops cannot shift a single artifact outcome that would otherwise have
 * happened. Suffixed rather than prefixed so the kill's identity still reads
 * first in an audit row.
 */
export function equipmentDropMessage(kill: KillIdentity): string {
  return `${dropIdFor(kill)}:equipment`;
}

/** The message that decides WHICH component, once one has dropped. */
export function equipmentSpeciesMessage(kill: KillIdentity): string {
  return `${dropIdFor(kill)}:equipment:species`;
}

/** Reads a 64-bit lane of a 256-bit digest as a bigint. */
function lane(digest: Uint8Array, index: number): bigint {
  let value = 0n;
  for (let byte = 0; byte < 8; byte += 1) {
    value = (value << 8n) | BigInt(digest[index * 8 + byte] ?? 0);
  }
  return value;
}

const LANE_SPACE = 1n << 64n;

/**
 * Uniform integer in `[0, bound)`, by rejection sampling.
 *
 * The naive `value % bound` is biased whenever `bound` does not divide 2^64,
 * and none of these bounds do: the lowest residues would come up once more
 * often than the rest, which for a rarity table means every component is
 * slightly better than the table says and for the drop roll means the item is
 * slightly more common than 1 in 6 000. Values at or above the largest exact
 * multiple are rejected and the next lane is tried.
 *
 * `null` means every available lane fell in the rejected tail. The caller must
 * treat that as NO drop rather than guessing, so a missing answer can never
 * invent an item.
 */
function uniformBelow(digest: Uint8Array, bound: bigint, firstLane: number): bigint | null {
  const limit = (LANE_SPACE / bound) * bound;
  for (let index = firstLane; index < 4; index += 1) {
    const value = lane(digest, index);
    if (value < limit) {
      return value % bound;
    }
  }
  return null;
}

/** Which species a kill's special roll produced. */
export interface EquipmentDropDecision {
  readonly kind: EquipmentDropKind;
}

/**
 * Decides the SPECIES of one kill's special roll: component, stabiliser or
 * nothing.
 *
 * One draw, one denominator, three ranges. The component range comes first and
 * the stabiliser range immediately after it, so the two cannot overlap however
 * the numbers are later tuned.
 */
export function decideEquipmentDrop(
  digest: Uint8Array,
  context: { readonly wasBoss: boolean; readonly bossTier?: BossTier },
): EquipmentDropDecision {
  const table = context.wasBoss
    ? BOSS_SPECIAL_DROP_ODDS_BY_TIER.byTier[context.bossTier ?? DEFAULT_BOSS_TIER]
    : EQUIPMENT_NORMAL_DROP;
  const denominator = BigInt(
    context.wasBoss
      ? BOSS_SPECIAL_DROP_ODDS_BY_TIER.denominator
      : EQUIPMENT_NORMAL_DROP.denominator,
  );

  const roll = uniformBelow(digest, denominator, 0);
  if (roll === null) {
    return { kind: 'none' };
  }
  if (roll < BigInt(table.componentOutcomes)) {
    return { kind: 'component' };
  }
  if (roll < BigInt(table.componentOutcomes + table.stabilizerOutcomes)) {
    return { kind: 'stabilizer' };
  }
  return { kind: 'none' };
}

/** Which component, and how good it is. */
export interface ComponentSpecies {
  readonly component: EquipmentComponent;
  readonly rarity: EquipmentRarity;
}

/**
 * Picks the component's type and rarity from a second digest.
 *
 * Type is uniform over the five - exactly 20% each. Nothing here looks at which
 * slots the account has filled, at what it is already wearing, or at what it
 * drew last time: duplicates are intended, and a table that avoided them would
 * be a pity system by another name.
 *
 * Type takes lanes 0 and 1, rarity takes 2 and 3, so each has a fallback if its
 * first lane is rejected and the two never read the same bits.
 */
export function pickComponentSpecies(
  digest: Uint8Array,
  /*
   * A tabela do MAPA, obrigatoria (gate V28I.BP).
   *
   * Isto tinha `MAP_1_COMPONENT_RARITY` por omissao, e era assim que os vinte
   * mapas partilhavam a distribuicao de um so. Um recuo silencioso e exactamente
   * o que este gate veio remover: quem chama tem de dizer de que mapa fala.
   */
  table: ComponentRarityTable,
): ComponentSpecies | null {
  const typePick = uniformBelow(digest, BigInt(EQUIPMENT_COMPONENTS.length), 0);
  const rarityPick = uniformBelow(digest, BigInt(table.denominator), 2);
  if (typePick === null || rarityPick === null) {
    return null;
  }
  const component = EQUIPMENT_COMPONENTS[Number(typePick)];
  if (!component) {
    return null;
  }

  let cursor = 0n;
  for (const band of table.bands) {
    cursor += BigInt(band.outcomes);
    if (rarityPick < cursor) {
      return { component, rarity: band.rarity };
    }
  }
  // Unreachable while the bands total the denominator, which a test asserts by
  // equality. Falling through would mean the table had been edited without it.
  return null;
}

/**
 * The future contract, stated in the type system today.
 *
 * A boss will grant between one and six weak items according to its size. When
 * that exists, a successful special roll OCCUPIES ONE of those positions rather
 * than adding a seventh - so the total stays inside the promised range. No weak
 * item is invented here; this only records which slot the special has claimed,
 * so the code that eventually fills the rest cannot double-count it.
 */
export interface BossLootBudget {
  readonly tier: BossTier;
  /** How many positions this boss will eventually award. */
  readonly totalPositions: number;
  /** Positions the special roll has taken. Zero or one, never more. */
  readonly positionsTakenBySpecial: number;
}

export function bossLootBudget(tier: BossTier, special: EquipmentDropKind): BossLootBudget {
  return {
    tier,
    // The size-to-count rule is NOT decided, so this reports the tier rather
    // than inventing a mapping. It exists so the future weak-item code has one
    // place to read "how many are left" from.
    totalPositions: tier,
    positionsTakenBySpecial: special === 'none' ? 0 : 1,
  };
}
